import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { Unless, When } from "react-if";
import { Skeleton } from "@mui/material";

import {
  useTouchIDAgentsService,
  useTouchIDInfoAgentsService,
} from "../../../../../services/microservice/micelu/touch/use-touch";
import PrimaryButton from "../../../../../components/buttons/PrimaryButton";
import { useInterval } from "../../../../../hooks/useInterval";
import { useKioskoConfig } from "../../../context/kiosko-config.context";

const ERROR_TOUCH = "La prueba aún no se ha completado en el celular.";
const ERROR_INTERNAL = "Acaba de ocurrir un problema, lo sentimos.";

interface ITouch {
  touchUrl: string;
  touchId: string;
}

interface IProps {
  imei: string;
  loading?: boolean;
  // "Asegurar" si el plan crea la póliza directo; "Siguiente" si va a pago.
  nextLabel?: string;
  onValidated: (touchId: string) => void;
  onBack: () => void;
}

const TouchComponent = ({
  imei,
  loading,
  nextLabel = "Siguiente",
  onValidated,
  onBack,
}: IProps) => {
  const config = useKioskoConfig();
  const [error, setError] = useState<string>("");
  const [touchInfo, setTouchInfo] = useState<ITouch>();
  const [validate, setValidate] = useState<boolean>(false);
  const { mutateAsync: getInfoTouchID, isLoading } =
    useTouchIDInfoAgentsService();
  const { mutateAsync: getTouchID, isLoading: isLoadingGetTouch } =
    useTouchIDAgentsService();

  const handleGetTouchID = async () => {
    setError("");
    try {
      const res = await getTouchID({
        imei,
        sponsorID: config.sponsorID,
      });
      setTouchInfo(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleValidate = async () => {
    if (!touchInfo?.touchId) return;
    try {
      const res = await getInfoTouchID({
        sponsorID: config.sponsorID,
        touchID: touchInfo.touchId,
      });
      if (res.data.isValidTouch) {
        setValidate(true);
        setError("");
      } else {
        setError(ERROR_TOUCH);
      }
    } catch (err: any) {
      if (err?.response?.status === 400) {
        setError(ERROR_TOUCH);
      } else {
        setError(ERROR_INTERNAL);
      }
    }
  };

  useEffect(() => {
    handleGetTouchID();
  }, []);

  useInterval(() => {
    if (!validate) {
      handleValidate();
    }
  }, 10000);

  return (
    <section className="w-full">
      <h2 className="mb-5 text-center text-3xl font-bold">
        <span className="text-buttonBlack">Prueba de dispositivo</span>
      </h2>
      <p className="text-xl font-normal">
        Para continuar, escanea el código QR con el celular que deseas asegurar
        y realiza la prueba. Recuerda que el código solo dura 5 minutos; si no
        la realizas a tiempo, deberás generar uno nuevo.
      </p>
      <div className="flex w-full flex-col items-center justify-center">
        <When condition={isLoadingGetTouch}>
          <div className="flex w-full justify-center">
            <Skeleton variant="rectangular" width={150} height={150} />
          </div>
        </When>
        <Unless condition={isLoadingGetTouch}>
          <QRCode
            value={touchInfo?.touchUrl || ""}
            className="my-5 block h-40 w-40"
          />
        </Unless>

        <div className="my-3 flex w-full flex-col justify-evenly md:flex-row">
          {!validate && (
            <PrimaryButton
              title="Generar nuevo código QR"
              onClick={() => handleGetTouchID()}
              className="rounded-2xl bg-buttonBlack px-5 text-white"
              loading={isLoadingGetTouch}
            />
          )}

          {validate ? (
            <div className="ml-10 flex items-center">
              <div className="mr-2 h-10 w-10">
                <svg
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 130.2 130.2"
                >
                  <circle
                    className="path circle"
                    fill="none"
                    stroke="#73AF55"
                    strokeWidth="6"
                    strokeMiterlimit="10"
                    cx="65.1"
                    cy="65.1"
                    r="62.1"
                  />
                  <polyline
                    className="path check"
                    fill="none"
                    stroke="#73AF55"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeMiterlimit="10"
                    points="100.2,40.2 51.5,88.8 29.8,67.5 "
                  />
                </svg>
              </div>
              <p className="text-lg">Prueba validada exitosamente</p>
            </div>
          ) : (
            <PrimaryButton
              title="Validar"
              onClick={() => handleValidate()}
              className="mt-5 block rounded-2xl bg-buttonBlack px-5 text-white md:mt-0"
              loading={isLoading}
            />
          )}
        </div>
        {error === ERROR_INTERNAL && (
          <p className="text-lg text-critical">{error}</p>
        )}
        {error && error !== ERROR_INTERNAL && (
          <div>
            <p className="text-lg">
              A la espera de que el cliente complete la prueba en el celular a
              asegurar
            </p>
            <div className="mt-2 flex items-center justify-center space-x-2">
              <div className="h-4 w-4 animate-bounce rounded-full bg-blueFinance [animation-delay:-0.3s]"></div>
              <div className="h-4 w-4 animate-bounce rounded-full bg-blueFinance [animation-delay:-0.15s]"></div>
              <div className="h-4 w-4 animate-bounce rounded-full bg-blueFinance"></div>
            </div>
          </div>
        )}
      </div>
      <div className="mt-4 flex justify-between">
        <PrimaryButton
          title="Atrás"
          className="rounded-2xl border px-5 text-textBlack"
          type="button"
          onClick={onBack}
        />
        <PrimaryButton
          title={nextLabel}
          className="rounded-2xl bg-buttonBlack px-5 text-white"
          style={{ opacity: validate ? 1 : 0.5 }}
          disabled={!validate || loading}
          loading={loading}
          onClick={() => touchInfo?.touchId && onValidated(touchInfo.touchId)}
        />
      </div>
    </section>
  );
};

export default TouchComponent;
